/**
 * Pure helpers behind the QC Set up page's "Estimated AI cost". Only the writing step has
 * usage to estimate from (the last finished QC run's token totals); every other AI step says
 * so plainly, and a placeholder price or a missing earlier run gives "No estimate yet".
 */
import type { GenerationRunRead, RunMetricsRead } from '../types/content.types';
import type {
  CatalogEntryRead,
  DecimalString,
  FlowSpecRead,
  ResolvedFlowRead,
} from '../types/model-configuration.types';

import { formatCostPerMillion, isPlaceholderPricing } from './format-cost';
import { runScores, runUnderstands, runWrites } from './run-summary';

/** The step whose usage is tracked well enough to estimate: writing content. */
const WRITING_FLOW = 'CONTENT_ENRICHMENT';

/** Steps known to call no model, so they read as free even before (or without) the flow list. */
export const KNOWN_FREE_FLOWS: ReadonlySet<string> = new Set(['CONTENT_DETERMINISTIC_SCORING']);

/** A `Decimal` price from the wire as a number, or null when it isn't a usable (finite, ≥ 0) one. */
export function decimalToNumber(value: DecimalString): number | null {
  const parsed = typeof value === 'number' ? value : Number.parseFloat(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

/** What one AI step runs on and what it costs per 1M tokens. */
export interface FlowPrice {
  model: string;
  inputPer1M: number | null;
  outputPer1M: number | null;
  /** True while the catalog row still carries the placeholder price. */
  placeholder: boolean;
}

/**
 * The resolved row a flow runs on for the chosen channel. Channel-scoped flows have one row
 * per channel; channel-agnostic flows have one row, so the first match is the answer.
 */
export function resolvedFlowFor(
  flows: ResolvedFlowRead[] | undefined,
  flow: string,
  platform: string,
): ResolvedFlowRead | undefined {
  const rows = (flows ?? []).filter((row) => row.flow === flow);
  return rows.find((row) => row.platform === platform) ?? rows[0];
}

/**
 * Model from the resolved configuration, price from the catalog row for that model (the
 * newest effective one); falls back to the price the resolved row carries when the catalog
 * has no matching row.
 */
export function flowPrice(
  resolved: ResolvedFlowRead | undefined,
  catalog: CatalogEntryRead[] | undefined,
): FlowPrice | null {
  if (!resolved) return null;
  const entry = (catalog ?? [])
    .filter((row) => row.model === resolved.model && row.vendor === resolved.vendor)
    // ISO timestamps sort as strings; a row with no date counts as oldest.
    .sort((a, b) => (b.effectiveFrom ?? '').localeCompare(a.effectiveFrom ?? ''))[0];
  const source = entry ?? resolved;
  return {
    model: resolved.model,
    inputPer1M: decimalToNumber(source.costPer1MInput),
    outputPer1M: decimalToNumber(source.costPer1MOutput),
    placeholder: isPlaceholderPricing(source.pricingSource),
  };
}

/** "$0.40 / $1.60 per 1M tokens (in/out)", or a plain note when the price isn't real yet. */
export function priceLabel(price: FlowPrice | null): string {
  if (!price) return 'Price unknown';
  if (price.placeholder || price.inputPer1M === null || price.outputPer1M === null) {
    return 'Price not set yet';
  }
  return `${formatCostPerMillion(price.inputPer1M)} / ${formatCostPerMillion(price.outputPer1M)} per 1M tokens (in/out)`;
}

/**
 * The QC run to estimate from: the newest finished one that wrote content and covered at
 * least one product. `runs` come newest first, so the first match wins.
 */
export function lastFinishedWritingRun(runs: GenerationRunRead[]): GenerationRunRead | undefined {
  return runs.find((run) => run.status === 'COMPLETED' && runWrites(run) && run.totalProducts > 0);
}

/**
 * The finished QC run whose pace times a new check: the last writing run when the check writes;
 * otherwise the last one that did the same kind of work (e.g. understanding only), so an
 * understanding check is never timed from a writing run. Undefined when there is none yet.
 */
export function lastFinishedRunLike(
  runs: GenerationRunRead[],
  sentFlows: string[],
): GenerationRunRead | undefined {
  if (sentFlows.length === 0) return undefined;
  const choice = { flows: sentFlows };
  if (runWrites(choice)) return lastFinishedWritingRun(runs);
  return runs.find(
    (run) =>
      run.status === 'COMPLETED' &&
      run.totalProducts > 0 &&
      !runWrites(run) &&
      runScores(run) === runScores(choice) &&
      runUnderstands(run) === runUnderstands(choice),
  );
}

/** Tokens one product used in a finished run (the run's total over its products). */
export interface TokensPerProduct {
  prompt: number;
  completion: number;
}

/**
 * Per-product token use from a run's metrics. The service reports one total for the whole
 * run, so this includes every step the run took — a slight overestimate for writing alone.
 */
export function tokensPerProduct(
  run: Pick<GenerationRunRead, 'totalProducts'> | undefined,
  metrics: RunMetricsRead | null | undefined,
): TokensPerProduct | null {
  if (!run || run.totalProducts <= 0 || !metrics) return null;
  const { promptTokens, completionTokens } = metrics.tokenUsage;
  // Missing or malformed counts would turn every estimate into NaN, so they mean "no basis".
  if (!Number.isFinite(promptTokens) || !Number.isFinite(completionTokens)) return null;
  if (promptTokens < 0 || completionTokens < 0 || promptTokens + completionTokens <= 0) return null;
  return {
    prompt: promptTokens / run.totalProducts,
    completion: completionTokens / run.totalProducts,
  };
}

/** Dollars for one product at a price, or null when the price can't be used. */
export function costPerProduct(tokens: TokensPerProduct, price: FlowPrice | null): number | null {
  if (!price || price.placeholder || price.inputPer1M === null || price.outputPer1M === null) {
    return null;
  }
  return (tokens.prompt * price.inputPer1M + tokens.completion * price.outputPer1M) / 1_000_000;
}

const USD = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

/** "$1,234.57", or "< $0.01" for amounts too small to show at two decimals; "—" if not a number. */
export function formatUsd(amount: number): string {
  if (!Number.isFinite(amount)) return '—';
  if (amount > 0 && amount < 0.01) return '< $0.01';
  return USD.format(amount);
}

/** What the breakdown says for one step. */
export type CostEstimate =
  | { kind: 'free' }
  | { kind: 'estimated'; amount: number; perProduct: boolean; tokensPerProduct: number }
  | { kind: 'not-tracked' }
  | { kind: 'no-estimate'; reason: string };

/** One breakdown row. */
export interface CostLine {
  flow: string;
  free: boolean;
  model: string;
  price: string;
  estimate: CostEstimate;
}

/** Everything the summary card needs to show the cost. */
export interface QcCostEstimate {
  lines: CostLine[];
  /** The one line under "Estimated AI cost". */
  total: string;
}

/** Inputs to {@link estimateQcCost}. */
export interface QcCostInputs {
  /** Every step the run takes, prerequisites included, in run order. */
  flows: string[];
  specs: FlowSpecRead[] | undefined;
  /** The price each AI step runs at (missing = not resolved yet). */
  prices: Map<string, FlowPrice | null>;
  /** Per-product use from the last finished QC run, or null when there is none. */
  basis: TokensPerProduct | null;
  /** How many products will run, when the choice fixes it (ids or a limit). */
  products: number | null;
}

/** Builds the breakdown and the headline line. Writing is the only estimated step. */
export function estimateQcCost(input: QcCostInputs): QcCostEstimate {
  const specByFlow = new Map((input.specs ?? []).map((spec) => [spec.flow, spec]));

  const lines = input.flows.map((flow): CostLine => {
    const free = specByFlow.get(flow)?.cost === 'FREE' || KNOWN_FREE_FLOWS.has(flow);
    const price = input.prices.get(flow) ?? null;
    if (free)
      return { flow, free, model: 'No AI', price: 'Channel rules', estimate: { kind: 'free' } };
    const base = { flow, free, model: price?.model ?? 'Model unknown', price: priceLabel(price) };
    if (flow !== WRITING_FLOW) return { ...base, estimate: { kind: 'not-tracked' } };
    if (!input.basis) {
      return {
        ...base,
        estimate: { kind: 'no-estimate', reason: 'No finished QC check to learn from yet' },
      };
    }
    const each = costPerProduct(input.basis, price);
    if (each === null) {
      return {
        ...base,
        estimate: { kind: 'no-estimate', reason: 'This model has no real price yet' },
      };
    }
    return {
      ...base,
      estimate: {
        kind: 'estimated',
        amount: input.products === null ? each : each * input.products,
        perProduct: input.products === null,
        tokensPerProduct: Math.round(input.basis.prompt + input.basis.completion),
      },
    };
  });

  const anyAi = lines.some((line) => !line.free);
  const writes = input.flows.includes(WRITING_FLOW);
  const otherAi = lines.some((line) => !line.free && line.flow !== WRITING_FLOW);
  const writing = lines.find((line) => line.estimate.kind === 'estimated')?.estimate;
  let total: string;
  if (input.flows.length === 0) total = '—';
  else if (!anyAi) total = 'Free · no AI steps';
  else if (writing?.kind === 'estimated') {
    total = `≈ ${formatUsd(writing.amount)}${writing.perProduct ? ' per product' : ''}${otherAi ? ' · writing only' : ''}`;
  } else if (writes) total = 'No estimate yet';
  else total = 'Not estimated yet';

  return { lines, total };
}
